import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import {
  DatePeriod,
  getReferenceDateFromStartPeriod,
  shouldDisableMonthGenerator,
  shouldDisableYearGenerator,
} from "./utils";
import DatePicker from "../../../../../../components/DatePickers/DatePicker/DatePicker";
import Asterisk from "../../../Asterisk/Asterisk";
import { headingSx } from "../../styles";

export interface RequestedShipMonthSelectorProps {
  startPeriod?: DatePeriod | null;
  endPeriod?: DatePeriod | null;
  onSelect?: (date: Dayjs | undefined) => void;
  selectedValue?: Dayjs;
  disabled?: boolean;
  showRequestShipMonthWarning?: boolean;
  handleOnClick?: () => void;
}

const RequestedShipMonthSelector = ({
  onSelect = () => {},
  selectedValue,
  startPeriod,
  endPeriod,
  disabled,
  showRequestShipMonthWarning = false,
  handleOnClick = () => {},
}: RequestedShipMonthSelectorProps) => {
  const theme = useTheme();

  const { t } = useTranslation("orders");

  const shouldDisableMonth = shouldDisableMonthGenerator(
    startPeriod,
    endPeriod
  );
  const shouldDisableYear = shouldDisableYearGenerator(startPeriod, endPeriod);
  const referenceDate = getReferenceDateFromStartPeriod(startPeriod);

  return (
    <>
      <Box sx={headingSx(theme)}>
        {t("orderEntry.summary.header.requestedShipMonth")}
        <Asterisk />
      </Box>
      <DatePicker
        key={`requested-ship-month-selector-${
          selectedValue ? "selected" : "unselected"
        }`}
        handleOnClick={() => handleOnClick()}
        placeholder={
          t("orderEntry.summary.header.datepicker.label") || "Select a month"
        }
        value={selectedValue}
        hideLabel
        error={showRequestShipMonthWarning}
        format="MMM-YYYY"
        onChange={onSelect}
        formControlProps={{
          disabled,
          fullWidth: true,
        }}
        textFieldProps={{
          sx: {
            width: "100%",
          },
          disabled,
        }}
        dateCalendarProps={{
          disabled,
          sx: {
            width: "100%",
            borderColor: theme.tokens.FuelColorAlertErrorIcon,
          },
          views: ["month", "year"],
          openTo: "month",
          referenceDate,
          shouldDisableMonth,
          shouldDisableYear,
          slotProps: {
            monthButton: {
              sx: {
                color: theme.tokens.FuelColorTextPrimary,
                fontWeight: theme.tokens.FuelFontWeightBase,
                fontSize: theme.tokens.FuelFontSizeMd,
                "&.Mui-disabled": {
                  color: theme.tokens.FuelColorTextDisabled,
                },
              },
            },
            yearButton: {
              sx: {
                color: theme.tokens.FuelColorTextPrimary,
                fontWeight: theme.tokens.FuelFontWeightBase,
                fontSize: theme.tokens.FuelFontSizeMd,
                "&.Mui-disabled": {
                  color: theme.tokens.FuelColorTextDisabled,
                },
              },
            },
          },
        }}
      />
      {showRequestShipMonthWarning && (
        <Typography
          data-testid="requested-ship-month-warning"
          sx={{
            color: theme.tokens.FuelColorAlertErrorIcon,
            marginBottom: theme.spacing(1),
            fontSize: theme.tokens.FuelFontSizeSm,
            marginTop: theme.spacing(1),
          }}
        >
          {t("orderEntry.summary.header.requestedShipMonth.warning")}
        </Typography>
      )}
    </>
  );
};

export default RequestedShipMonthSelector;
